// `useIssueTimeline` — Phase 2d. Lazy GET for the timeline strip the
// editor renders below the AC field in edit mode.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  IssueTimelineResponseSchema,
  type IssueTimelineEvent,
} from "@loom/shared/dashboard";

import { httpGet, UnauthorizedError } from "../lib/http";

export interface IssueTimelineState {
  readonly events: ReadonlyArray<IssueTimelineEvent>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refetch: () => void;
}

export const useIssueTimeline = (
  projectId: number,
  iid: number | null,
): IssueTimelineState => {
  const [events, setEvents] = useState<ReadonlyArray<IssueTimelineEvent>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bump, setBump] = useState(0);
  const cancelled = useRef(false);

  const refetch = useCallback(() => setBump((n) => n + 1), []);

  useEffect(() => {
    cancelled.current = false;
    if (iid === null) {
      setEvents([]);
      return;
    }
    setLoading(true);
    setError(null);
    httpGet(
      `/api/projects/${projectId}/issues/${iid}/timeline`,
      IssueTimelineResponseSchema,
    )
      .then((res) => {
        if (cancelled.current) return;
        setEvents(res.events);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled.current) return;
        if (err instanceof UnauthorizedError) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      cancelled.current = true;
    };
  }, [projectId, iid, bump]);

  return { events, loading, error, refetch };
};
